import {
  inferDeviceIdType,
  isEquivalentDeviceIdType,
} from "./vastEnvironmentSignals";

export interface VastConsentSignals {
  gdpr?: string | undefined;
  gdprConsent?: string | undefined;
  usPrivacy?: string | undefined;
}

export interface VastMacroContext {
  correlator: string;
  streamCorrelator: string;
  pod?: number | undefined;
  podMaxAds?: number | undefined;
  podMinDurationMs?: number | undefined;
  podMaxDurationMs?: number | undefined;
  isCtv?: boolean | undefined;
  contentUrl?: string | undefined;
  pageUrl?: string | undefined;
  adWillPlayMuted?: boolean | undefined;
  adWillAutoPlay?: boolean | undefined;
  appId?: string | undefined;
  appName?: string | undefined;
  sessionId?: string | undefined;
  deviceId?: string | undefined;
  deviceIdType?: string | undefined;
  limitAdTracking?: boolean | undefined;
  deviceTypeHint?: number | undefined;
  adTest?: boolean | undefined;
  consent?: VastConsentSignals | undefined;
}

const UNEXPANDED_MACRO_PATTERN = /^(\[[^\]]*\]|\{[^}]*\}|%%[^%]*%%)$/;
const INVALID_VAST_PARAM_KEY = /^\s|\s$/;
const EXAMPLE_COM_URL = /example\.com/i;
const CTV_UNSUPPORTED_PARAMS = [
  "description_url",
  "tfcd",
  "npa",
  "min_ad_duration",
  "max_ad_duration",
  "unviewed_position_start",
  "impl",
  "vad_type",
  "gdpr",
  "gdpr_consent",
  "us_privacy",
];

const INLINE_DEVICE_MACRO_REPLACEMENTS: Array<{
  pattern: RegExp;
  getValue: (ctx: VastMacroContext) => string | undefined;
}> = [
  { pattern: /\[RDID\]/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /\[IFA\]/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /\[ADVERTISING[_-]?ID(?:ENTIFIER)?\]/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /\[DEVICE[_-]?ID\]/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /\[IDTYPE\]/gi, getValue: (ctx) => ctx.deviceIdType },
  { pattern: /\{rdid\}/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /\{device[_-]?id\}/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /\{idtype\}/gi, getValue: (ctx) => ctx.deviceIdType },
  { pattern: /%%ADVERTISING_IDENTIFIER_PLAIN%%/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /%%ADVERTISING_ID_OPT_OUT%%/gi, getValue: (ctx) =>
      ctx.limitAdTracking != null ? (ctx.limitAdTracking ? "1" : "0") : undefined },
  { pattern: /%%ADVERTISING_ID_TYPE%%/gi, getValue: (ctx) => ctx.deviceIdType },
  { pattern: /%%ADVERTISING_ID%%/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /%%LIMIT_AD_TRACKING%%/gi, getValue: (ctx) =>
      ctx.limitAdTracking != null ? (ctx.limitAdTracking ? "1" : "0") : undefined },
  { pattern: /%%MSID%%/gi, getValue: (ctx) => ctx.appId },
  { pattern: /%%APP_NAME%%/gi, getValue: (ctx) => ctx.appName },
  { pattern: /%%SESSION_ID%%/gi, getValue: (ctx) => ctx.sessionId },
  { pattern: /%%CORRELATOR%%/gi, getValue: (ctx) => ctx.correlator },
];

export function isMacroPlaceholder(value: string | null | undefined): boolean {
  if (value == null) return true;
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (UNEXPANDED_MACRO_PATTERN.test(trimmed)) return true;
  return /^(unknown|null|undefined|none|n\/a|\$\{[^}]+\})$/i.test(trimmed);
}

export function generateCorrelator(): string {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    try {
      const buf = new Uint32Array(2);
      crypto.getRandomValues(buf);
      const value = (buf[0] ?? 0) * 0x200000 + ((buf[1] ?? 0) & 0x1fffff);
      if (value > 0) {
        return String(value);
      }
    } catch {
    }
  }
  return String(Math.floor(Math.random() * Number.MAX_SAFE_INTEGER) + 1);
}

function expandInlineMacroPlaceholders(baseUrl: string, ctx: VastMacroContext): string {
  let url = baseUrl;
  for (const { pattern, getValue } of INLINE_DEVICE_MACRO_REPLACEMENTS) {
    const value = getValue(ctx);
    if (value != null && value !== "") {
      url = url.replace(pattern, encodeURIComponent(value));
    }
  }
  return url;
}

function applyDeviceIdentityParams(
  params: URLSearchParams,
  ctx: VastMacroContext
): void {
  const runtimeId = ctx.deviceId?.trim();
  const runtimeType = ctx.deviceIdType?.trim() || inferDeviceIdType();
  const tagRdid = params.get("rdid");
  const tagType = params.get("idtype");
  const tagLat = params.get("is_lat");
  const latValue =
    ctx.limitAdTracking != null ? (ctx.limitAdTracking ? "1" : "0") : undefined;

  if (runtimeId) {
    params.set("rdid", runtimeId);
    params.set("idtype", runtimeType || tagType || "aaid");
    params.set("is_lat", latValue ?? (isMacroPlaceholder(tagLat) ? "0" : tagLat!));
    return;
  }

  const tagRdidValid = Boolean(tagRdid && !isMacroPlaceholder(tagRdid));
  const tagTypeValid = Boolean(tagType && !isMacroPlaceholder(tagType));

  if (
    tagRdidValid &&
    tagTypeValid &&
    runtimeType &&
    !isEquivalentDeviceIdType(tagType!, runtimeType)
  ) {
    params.delete("rdid");
    params.delete("idtype");
    params.delete("is_lat");
    return;
  }

  if (tagRdidValid) {
    if (isMacroPlaceholder(tagType) && runtimeType) {
      params.set("idtype", runtimeType);
    }
    if (isMacroPlaceholder(tagLat)) {
      params.set("is_lat", latValue ?? "0");
    }
    return;
  }

  params.delete("rdid");
  params.delete("idtype");
  params.delete("is_lat");
}

function removeBrokenAppNameOrphans(
  params: URLSearchParams,
  appName?: string
): void {
  const toDelete: string[] = [];

  params.forEach((_value, key) => {
    if (!key || INVALID_VAST_PARAM_KEY.test(key)) {
      toDelete.push(key);
    }
  });

  if (appName && appName.includes("&")) {
    for (const suffix of appName.split("&").slice(1)) {
      const trimmed = suffix.replace(/^\s+/, "");
      if (!trimmed) continue;
      toDelete.push(suffix, ` ${trimmed}`, trimmed);
    }
  }

  for (const key of new Set(toDelete)) {
    if (key) params.delete(key);
  }
}

export function normalizeVastTagUrl(raw: string): string {
  if (!raw) return raw;

  try {
    const url = new URL(raw);

    const anValue = url.searchParams.get("an");
    if (anValue !== null) {
      const orphanFragments: string[] = [];
      url.searchParams.forEach((_value, key) => {
        if (key && INVALID_VAST_PARAM_KEY.test(key)) {
          orphanFragments.push(key.trim());
        }
      });
      if (orphanFragments.length > 0) {
        const reconstructed = [anValue.replace(/\s+$/, ""), ...orphanFragments]
          .filter(Boolean)
          .join(" & ");

        url.searchParams.forEach((_value, key) => {
          if (key && INVALID_VAST_PARAM_KEY.test(key)) {
            url.searchParams.delete(key);
          }
        });
        url.searchParams.set("an", reconstructed);

        return url.toString().replace(
          /([?&]an=)([^&]*)/,
          (_match, prefix, val) => prefix + val.replace(/\+/g, "%20")
        );
      }
    }

    removeBrokenAppNameOrphans(url.searchParams);
    return url.toString();
  } catch {
    return raw;
  }
}

export function applyVastMacros(
  baseUrl: string,
  ctx: VastMacroContext
): string {
  const expandedUrl = expandInlineMacroPlaceholders(baseUrl, ctx);
  let url: URL;
  try {
    url = new URL(normalizeVastTagUrl(expandedUrl));
  } catch {
    return replaceCorrelatorFallback(expandedUrl, ctx.correlator);
  }

  const params = url.searchParams;

  removeBrokenAppNameOrphans(params, ctx.appName);

  params.set("correlator", ctx.correlator);
  params.set("scor", ctx.streamCorrelator);
  if (ctx.isCtv) {
    for (const key of CTV_UNSUPPORTED_PARAMS) {
      params.delete(key);
    }
  }

  if (ctx.pod != null) {
    params.set("pod", String(ctx.pod));
  }

  params.delete("ppos");
  params.delete("lip");
  if (ctx.podMinDurationMs != null) {
    params.set("pmnd", String(Math.max(0, Math.round(ctx.podMinDurationMs))));
  }
  if (ctx.podMaxDurationMs != null) {
    params.set("pmxd", String(Math.max(0, Math.round(ctx.podMaxDurationMs))));
  }
  if (ctx.podMaxAds != null) {
    params.set("pmad", String(Math.max(1, Math.round(ctx.podMaxAds))));
  }

  const requestUrl = ctx.contentUrl || ctx.pageUrl;
  if (requestUrl) {
    params.set("url", requestUrl);
    if (!ctx.isCtv) {
      params.set("description_url", requestUrl);
    }
  } else {
    const existingUrl = params.get("url") || "";
    if (EXAMPLE_COM_URL.test(existingUrl)) {
      params.delete("url");
      params.delete("description_url");
    }
  }

  if (ctx.adWillPlayMuted != null) {
    params.set("vpmute", ctx.adWillPlayMuted ? "1" : "0");
  }
  if (ctx.adWillAutoPlay != null) {
    params.set("vpa", ctx.adWillAutoPlay ? "auto" : "click");
  }

  if (ctx.appId) {
    params.set("msid", ctx.appId);
  }
  if (ctx.appName) {
    removeBrokenAppNameOrphans(params, ctx.appName);
    params.set("an", ctx.appName);
  }
  if (ctx.sessionId) {
    params.set("sid", ctx.sessionId);
  }
  if (ctx.deviceTypeHint != null) {
    params.set("dth", String(ctx.deviceTypeHint));
  } else if (ctx.isCtv) {
    params.set("dth", "5");
  }

  applyDeviceIdentityParams(params, ctx);

  if (!ctx.isCtv) {
    const consent = ctx.consent;
    if (consent?.gdpr != null) {
      params.set("gdpr", consent.gdpr);
    }
    if (consent?.gdprConsent != null) {
      params.set("gdpr_consent", consent.gdprConsent);
    }
    if (consent?.usPrivacy != null) {
      params.set("us_privacy", consent.usPrivacy);
    }
  }

  if (ctx.adTest) {
    params.set("adtest", "on");
  }

  const staleKeys: string[] = [];
  params.forEach((value, key) => {
    if (isMacroPlaceholder(value)) {
      staleKeys.push(key);
    }
  });
  for (const key of staleKeys) {
    params.delete(key);
  }

  removeBrokenAppNameOrphans(params, ctx.appName);

  return url.toString();
}

function replaceCorrelatorFallback(baseUrl: string, correlator: string): string {
  const correlatorRegex = /([?&])correlator=([^&]*)/;
  if (correlatorRegex.test(baseUrl)) {
    return baseUrl.replace(correlatorRegex, `$1correlator=${correlator}`);
  }
  const sep = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${sep}correlator=${correlator}`;
}

export function fetchConsentSignals(
  timeoutMs: number = 1500
): Promise<VastConsentSignals> {
  const signals: VastConsentSignals = {};
  if (typeof window === "undefined") {
    return Promise.resolve(signals);
  }

  const tasks: Promise<void>[] = [];

  const tcfApi = (window as any).__tcfapi;
  if (typeof tcfApi === "function") {
    tasks.push(
      new Promise<void>((resolve) => {
        let settled = false;
        try {
          tcfApi("addEventListener", 2, (tcData: any, success: boolean) => {
            if (settled) return;
            if (
              success &&
              tcData &&
              (tcData.eventStatus === "tcloaded" ||
                tcData.eventStatus === "useractioncomplete")
            ) {
              settled = true;
              signals.gdpr = tcData.gdprApplies ? "1" : "0";
              if (typeof tcData.tcString === "string" && tcData.tcString) {
                signals.gdprConsent = tcData.tcString;
              }
              try {
                tcfApi("removeEventListener", 2, () => {}, tcData.listenerId);
              } catch {}
              resolve();
            }
          });
        } catch {
          resolve();
        }
        setTimeout(() => {
          if (!settled) {
            settled = true;
            resolve();
          }
        }, timeoutMs);
      })
    );
  }

  const uspApi = (window as any).__uspapi;
  if (typeof uspApi === "function") {
    tasks.push(
      new Promise<void>((resolve) => {
        try {
          uspApi("getUSPData", 1, (data: any, success: boolean) => {
            if (success && typeof data?.uspString === "string" && data.uspString) {
              signals.usPrivacy = data.uspString;
            }
            resolve();
          });
        } catch {
          resolve();
        }
        setTimeout(resolve, timeoutMs);
      })
    );
  }

  if (tasks.length === 0) {
    return Promise.resolve(signals);
  }

  return Promise.all(tasks).then(() => signals);
}
